import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from '@/utils/tailwind';
import { WebButton14 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';

const ANIMATION_DURATION_MS = 150;

enum Direction {
  UP = 'up',
  DOWN = 'down',
}

type CarouselTogglerOption<OptionId> = {
  id: OptionId;
  label: string;
};

export type CarouselTogglerProps<OptionId> = {
  options: CarouselTogglerOption<OptionId>[];
  value: OptionId;
  onChange: (optionId: OptionId) => void;
  disabled?: boolean;
  animateOnAppear?: boolean;
  className?: string;
};

const CarouselToggler = <OptionId extends string>({
  options,
  value: selectedOptionId,
  onChange: onSelectedOptionChange,
  disabled = false,
  animateOnAppear = false,
  className = '',
}: CarouselTogglerProps<OptionId>) => {
  const isFirstInteraction = useRef(true);
  const [direction, setDirection] = useState<Direction>(Direction.DOWN);

  const selectedOptionIndex = options.findIndex((option) => option.id === selectedOptionId);
  const selectedOption = options[Math.max(selectedOptionIndex, 0)];

  const longestLabel = useMemo(
    () =>
      options.reduce(
        (longest, option) => (option.label.length > longest.length ? option.label : longest),
        '',
      ),
    [options],
  );

  const handleClick = () => {
    if (disabled) {
      return;
    }

    let nextIndex = selectedOptionIndex;

    if (direction === Direction.DOWN) {
      nextIndex = Math.min(selectedOptionIndex + 1, options.length - 1);
    } else {
      nextIndex = Math.max(selectedOptionIndex - 1, 0);
    }

    onSelectedOptionChange(options[nextIndex].id);

    isFirstInteraction.current = false;
  };

  useLayoutEffect(() => {
    let nextDirection = direction;

    if (selectedOptionIndex >= options.length - 1) {
      nextDirection = Direction.UP;
    }

    if (selectedOptionIndex <= 0) {
      nextDirection = Direction.DOWN;
    }

    setDirection(nextDirection);
  }, [selectedOptionIndex]);

  const shouldAnimate = !isFirstInteraction.current || animateOnAppear;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn('flex items-center justify-end gap-1 p-2 pr-0', className)}
      aria-label={selectedOption.label}
      disabled={disabled}
    >
      <div className="inline-flex relative">
        {options.map((option, index) => {
          const distanceFromSelectedOption = selectedOptionIndex - index;

          return (
            <span
              key={option.id}
              className={cn(
                'text-primary inline-flex opacity-0 absolute right-0',
                shouldAnimate && 'transition-all ease-out',
                index === selectedOptionIndex && 'opacity-100',
                disabled && 'text-grey55',
              )}
              style={{
                transform: `translateY(${distanceFromSelectedOption * -100}%)`,
                transitionDuration: `${ANIMATION_DURATION_MS}ms`,
              }}
            >
              <WebButton14.Bold className="leading-5">{option.label}</WebButton14.Bold>
            </span>
          );
        })}

        {/* Invisible text to reserve space for the longest label */}
        <WebButton14.Bold className="inline-flex leading-5 opacity-0">
          {longestLabel}
        </WebButton14.Bold>
      </div>

      {/* Dots container */}
      <div className="w-1.5 flex flex-col items-center gap-1">
        {options.map((option) => (
          <div
            key={option.id}
            className={cn(
              'w-1 h-1 rounded-full bg-grey25',
              shouldAnimate && 'transition-all ease-out',
              option.id === selectedOptionId && 'w-1.5 h-1.5 bg-primary',
              option.id === selectedOptionId && disabled && 'bg-grey41',
            )}
            style={{
              transitionDuration: `${ANIMATION_DURATION_MS}ms`,
            }}
          />
        ))}
      </div>
    </button>
  );
};

export default CarouselToggler;
